/**
 * LifecycleStepCards Component
 *
 * Block diagram of the lifecycle — the overview layout. Every step opens its
 * own detail; steps without settings of their own carry a lock.
 */
import { cn, Icon } from '@insio/ui-toolkit';
import { useFeatureTranslation } from '@/hooks/useFeatureTranslation';
import {
  CREATION_ICON,
  isStepOff,
  stepLabelKey,
  type LifecycleDocument,
  type LifecycleStep,
  type LifecycleStepKey
} from './types';

export interface LifecycleStepCardsProps {
  /** Document whose lifecycle is drawn. */
  document: LifecycleDocument;
  /** Steps of the lifecycle with their automation state. */
  steps: LifecycleStep[];
  /** Opens the detail of a step. */
  onSelectStep: (step: LifecycleStepKey) => void;
  /** Id of the heading labelling the diagram. */
  labelId: string;
  /** Card slots on wide screens; those beyond the steps stay empty. Defaults to the steps. */
  slots?: number;
}

/** State line of a card — a lock label on locked steps, a status dot otherwise. */
function StepState({ step }: { step: LifecycleStep }) {
  if (step.isLocked) {
    return (
      <span className='flex items-center gap-xs'>
        <Icon name='lock' className='w-3 h-3 text-foreground-disabled' />
        <span className='text-xs2 font-medium text-foreground-disabled'>{step.stateLabel}</span>
      </span>
    );
  }

  return (
    <span className='flex items-center gap-xs'>
      <span
        className={cn(
          'w-2 h-2 rounded-full',
          step.isEnabled ? 'bg-success-border' : 'bg-foreground-disabled'
        )}
      />
      <span
        className={cn(
          'text-xs2 font-medium',
          step.isEnabled ? 'text-success-text' : 'text-foreground-disabled'
        )}
      >
        {step.stateLabel}
      </span>
    </span>
  );
}

export function LifecycleStepCards({
  document,
  steps,
  onSelectStep,
  labelId,
  slots = steps.length
}: LifecycleStepCardsProps) {
  const t = useFeatureTranslation('systemSettings.automation.lifecycle');
  const label = (step: LifecycleStep, field: string) =>
    t(`steps.${stepLabelKey(document, step.key)}.${field}`);

  return (
    <nav aria-labelledby={labelId}>
      {/* Horizontal diagram — wide screens */}
      <div className='hidden lg:flex items-stretch w-full'>
        <div className='flex items-center flex-none'>
          <span
            className='w-11 h-11 rounded-full bg-background-muted flex items-center justify-center'
            title={t(`steps.${document}Input`)}
          >
            <Icon name={CREATION_ICON[document]} className='w-5 h-5 text-foreground-muted' />
          </span>
        </div>

        {steps.map(step => (
          <div key={step.key} className='flex items-stretch flex-1 min-w-0'>
            <span className='flex items-center px-xs flex-none self-center'>
              <Icon name='arrow-right' className='w-4 h-4 text-foreground-disabled' />
            </span>
            <button
              type='button'
              onClick={() => onSelectStep(step.key)}
              data-testid={`lifecycle-${document}-step-${step.key}`}
              className={cn(
                'flex-1 min-w-0 flex flex-col gap-sm p-md text-left rounded-xl border cursor-pointer transition-all hover:border-primary-base',
                isStepOff(step)
                  ? 'bg-background-base border-dashed border-border-base'
                  : 'bg-background-input border-border-input shadow-sm'
              )}
            >
              <span className='flex items-start justify-between'>
                <span
                  className={cn(
                    'w-9 h-9 rounded-lg flex items-center justify-center',
                    isStepOff(step) ? 'bg-background-input' : 'bg-background-base'
                  )}
                >
                  <Icon
                    name={step.icon}
                    className={cn(
                      'w-5 h-5',
                      isStepOff(step) ? 'text-foreground-disabled' : 'text-foreground-base'
                    )}
                  />
                </span>
                <Icon
                  name={step.isLocked ? 'lock' : 'arrow-right'}
                  className='w-4 h-4 text-foreground-disabled'
                />
              </span>
              <span className='flex flex-col gap-2xs'>
                <span
                  className={cn(
                    'text-sm font-semibold',
                    isStepOff(step) ? 'text-foreground-muted' : 'text-foreground-base'
                  )}
                >
                  {label(step, 'title')}
                </span>
                <span className='text-xs2 leading-snug text-foreground-muted'>
                  {label(step, 'description')}
                </span>
              </span>
              <span className='mt-auto'>
                <StepState step={step} />
              </span>
            </button>
          </div>
        ))}
        {Array.from({ length: Math.max(0, slots - steps.length) }, (_, index) => (
          <div key={index} className='flex-1 min-w-0' aria-hidden='true' />
        ))}
      </div>

      {/* Vertical stepper — small screens */}
      <div className='lg:hidden flex flex-col'>
        <div className='flex gap-md'>
          <div className='flex flex-col items-center w-7 flex-none'>
            <span
              className='w-7 h-7 rounded-full bg-background-muted flex items-center justify-center'
              title={t(`steps.${document}Input`)}
            >
              <Icon name={CREATION_ICON[document]} className='w-4 h-4 text-foreground-muted' />
            </span>
            <span className='w-0.5 h-5 bg-border-base' />
          </div>
          <div />
        </div>

        {steps.map((step, idx) => (
          <div key={step.key} className='flex gap-md'>
            <div className='flex flex-col items-center w-7 flex-none'>
              <span
                className={cn(
                  'w-7 h-7 rounded-full text-xs font-semibold flex items-center justify-center',
                  isStepOff(step)
                    ? 'border border-dashed border-border-base text-foreground-disabled'
                    : 'bg-background-input text-foreground-base'
                )}
              >
                {idx + 1}
              </span>
              {idx < steps.length - 1 && <span className='w-0.5 flex-1 bg-border-base' />}
            </div>
            <button
              type='button'
              onClick={() => onSelectStep(step.key)}
              data-testid={`lifecycle-${document}-step-mobile-${step.key}`}
              className={cn(
                'flex-1 mb-md flex items-center gap-md p-md text-left rounded-xl border cursor-pointer transition-all hover:border-primary-base',
                isStepOff(step)
                  ? 'bg-background-base border-dashed border-border-base'
                  : 'bg-background-input border-border-input shadow-sm'
              )}
            >
              <span className='flex-1 flex flex-col gap-2xs'>
                <span
                  className={cn(
                    'text-sm font-semibold',
                    isStepOff(step) ? 'text-foreground-muted' : 'text-foreground-base'
                  )}
                >
                  {label(step, 'title')}
                </span>
                <span className='text-xs leading-snug text-foreground-muted'>
                  {label(step, 'description')}
                </span>
              </span>
              <StepState step={step} />
              <Icon
                name={step.isLocked ? 'lock' : 'arrow-right'}
                className='w-4 h-4 text-foreground-disabled flex-none'
              />
            </button>
          </div>
        ))}
      </div>
    </nav>
  );
}
